import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { getProject, getMasters, deleteMaster, MASTER_STATUSES, errorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { MasterCards } from '../components/MasterCards';
import { 
    ArrowLeft,
    Plus,
    Edit,
    Star,
    Archive,
    Film,
    HardDrive,
    MoreVertical,
    Trash2,
    Printer,
    Lock,
    Unlock,
    Clock
} from 'lucide-react';

export default function ProjectDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isManager, isAdmin } = useAuth();
    const [project, setProject] = useState(null);
    const [masters, setMasters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteId, setDeleteId] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when these inputs change; the loader is recreated each render and reads current state
    }, [id]);

    const fetchData = async () => {
        try {
            const [projectRes, mastersRes] = await Promise.all([
                getProject(id),
                getMasters({ project_id: id })
            ]);
            setProject(projectRes.data);
            setMasters(mastersRes.data);
        } catch (error) {
            if (!error._isAuthError) toast.error(errorMessage(error, 'Erreur lors du chargement du projet'));
            navigate('/projects');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteMaster = async () => {
        if (!deleteId) return;
        try {
            await deleteMaster(deleteId);
            toast.success('Master supprimé');
            setDeleteId(null);
            fetchData();
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de la suppression'));
        }
    };

    const getStatusBadgeClass = (status) => {
        return `status-${status}`;
    };

    if (loading) {
        return (
            <Layout>
                <div className="p-4 sm:p-6 space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-64" />
                </div>
            </Layout>
        );
    }

    if (!project) return null;

    const renderMasterActions = (master) => (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={`Actions pour ${master.name}`} className="h-8 w-8">
                    <MoreVertical className="w-4 h-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate(`/masters/${master.id}`)}>
                    <HardDrive className="w-4 h-4 mr-2" />
                    Détails
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(`/masters/${master.id}/label`)}>
                    <Printer className="w-4 h-4 mr-2" />
                    Imprimer étiquette
                </DropdownMenuItem>
                {isManager() && (
                    <>
                        <DropdownMenuItem onClick={() => navigate(`/masters/${master.id}/edit`)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Modifier
                        </DropdownMenuItem>
                        {isAdmin() && (
                            <DropdownMenuItem 
                                onClick={() => setDeleteId(master.id)}
                                className="text-destructive-text focus:text-destructive-text"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer
                            </DropdownMenuItem>
                        )}
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );

    return (
        <Layout>
            <div className="p-4 sm:p-6 space-y-6 animate-fade-in" data-testid="project-detail">
                {/* Header */}
                <div className="flex flex-wrap items-center gap-4">
                    <Button variant="ghost" size="icon" aria-label="Retour aux projets" onClick={() => navigate('/projects')} data-testid="back-button">
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="flex-1 min-w-[12rem]">
                        <div className="flex items-center gap-3">
                            <h1 className="font-heading text-3xl font-bold tracking-tight break-words min-w-0">
                                {project.title}
                            </h1>
                            {project.favorite && (
                                <Star className="w-5 h-5 text-primary fill-primary" />
                            )}
                            {project.archived && (
                                <Badge variant="secondary">
                                    <Archive className="w-3 h-3 mr-1" />
                                    Archivé{project.archive_destination ? ` — ${project.archive_destination}` : ''}
                                </Badge>
                            )}
                        </div>
                        <p className="text-muted-foreground">
                            {project.original_title && project.original_title !== project.title && (
                                <span className="italic">{project.original_title} • </span>
                            )}
                            {project.production_year || 'Année N/A'}
                        </p>
                    </div>
                    {isManager() && (
                        <div className="flex flex-wrap gap-2">
                            <Button variant="outline" onClick={() => navigate(`/projects/${id}/edit`)} data-testid="edit-project-button">
                                <Edit className="w-4 h-4 mr-2" />
                                Modifier
                            </Button>
                            <Button onClick={() => navigate(`/projects/${id}/masters/new`)} data-testid="new-master-button">
                                <Plus className="w-4 h-4 mr-2" />
                                Nouveau master
                            </Button>
                        </div>
                    )}
                </div>

                {/* Project Info */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Poster */}
                    <Card className="bg-card border-border lg:col-span-1">
                        <CardContent className="p-4">
                            {project.poster_base64 ? (
                                <img
                                    src={project.poster_base64}
                                    alt={project.title}
                                    className="w-full aspect-[2/3] object-cover rounded"
                                />
                            ) : (
                                <div className="w-full aspect-[2/3] bg-secondary rounded flex items-center justify-center">
                                    <Film className="w-16 h-16 text-muted-foreground/30" />
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Masters Table */}
                    <Card className="bg-card border-border lg:col-span-3">
                        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                            <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
                                <HardDrive className="w-5 h-5" />
                                Masters ({masters.length})
                            </CardTitle>
                            {masters.length > 0 && (
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="w-[180px]" data-testid="status-filter-select">
                                        <SelectValue placeholder="Filtrer par statut" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous les statuts</SelectItem>
                                        {Object.entries(MASTER_STATUSES).map(([key, label]) => (
                                            <SelectItem key={key} value={key}>{label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </CardHeader>
                        <CardContent>
                            {masters.length === 0 ? (
                                <div className="text-center py-12">
                                    <HardDrive className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                                    <p className="text-muted-foreground mb-4">Aucun master pour ce projet</p>
                                    {isManager() && (
                                        <Button onClick={() => navigate(`/projects/${id}/masters/new`)}>
                                            <Plus className="w-4 h-4 mr-2" />
                                            Créer le premier master
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <>
                                <MasterCards
                                    masters={masters.filter(m => statusFilter === 'all' || m.status === statusFilter)}
                                    statusClass={getStatusBadgeClass}
                                    renderActions={renderMasterActions}
                                />
                                <div className="hidden sm:block rounded-sm border border-border overflow-hidden">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-muted/50 hover:bg-muted/50">
                                                <TableHead className="font-bold text-xs uppercase tracking-wider">Nom du master</TableHead>
                                                <TableHead className="font-bold text-xs uppercase tracking-wider">N° Disque</TableHead>
                                                <TableHead className="font-bold text-xs uppercase tracking-wider">Version</TableHead>
                                                <TableHead className="font-bold text-xs uppercase tracking-wider">Durée</TableHead>
                                                <TableHead className="font-bold text-xs uppercase tracking-wider">Format</TableHead>
                                                <TableHead className="font-bold text-xs uppercase tracking-wider">Statut</TableHead>
                                                <TableHead className="font-bold text-xs uppercase tracking-wider w-[100px]">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {masters.filter(m => statusFilter === 'all' || m.status === statusFilter).map((master) => (
                                                <TableRow 
                                                    key={master.id} 
                                                    className="hover:bg-muted/30 cursor-pointer"
                                                    onClick={() => navigate(`/masters/${master.id}`)}
                                                    data-testid={`master-row-${master.id}`}
                                                >
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <Link
                                                                to={`/masters/${master.id}`}
                                                                onClick={(e) => e.stopPropagation()}
                                                                title={master.name}
                                                                className="font-mono text-sm truncate max-w-[300px] hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
                                                            >
                                                                {master.name}
                                                            </Link>
                                                            {master.encrypted && (
                                                                <Lock className="w-3 h-3 text-tone-ecrase" />
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="font-mono text-sm">{master.disk_number}</TableCell>
                                                    <TableCell className="font-mono text-sm">{master.version || '-'}</TableCell>
                                                    <TableCell className="font-mono text-sm">{master.duration_tc}</TableCell>
                                                    <TableCell className="text-sm">{master.format}</TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className={`text-xs ${getStatusBadgeClass(master.status)}`}>
                                                            {MASTER_STATUSES[master.status] || master.status}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                                        {renderMasterActions(master)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Delete Dialog */}
            <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer ce master ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Cette action est irréversible. Tous les mouvements associés seront également supprimés.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteMaster} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}
